import { reactive, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import { object, string, number, array, date } from 'yup'
import { useReceipts } from './useReceipts'
import { useReceiptDialog } from './useReceiptDialog'
import { useReceiptReject } from './useReceiptReject'
import { useNotifications } from '@/services/notification/useNotifications'
import { useDialog } from '@/services/useDialog'
import { useLogs } from '@/services/useLogs'
import { useTopData } from '@/services/useHome'
import { useLoader } from '../useLoader'
import { useError } from '../useError'
import axios from '@/axios'
import dayjs from 'dayjs'
import { expenseTypesAdjust } from '@/services/useExpenseType'

const tabs = reactive([
  {
    name: '基本情報',
    required: true,
    status: 0,
    fields: [
      'expenseTypeId',
      'expenseDate',
      'expenseAmount',
      'expenseContent1',
      'expenseContent2',
      'denominator',
      'numerator',
      'isDistributed',
    ],
  },

  { name: '領収書添付', required: true, status: 0 },
  { name: '事務局より', required: true, status: 0 },
])

const expenseTypes = reactive(expenseTypesAdjust())

export const useReceiptFormTabs = () => {
  return { tabs }
}

export const dateComparison = () => {
  const { currentReceipt } = useReceipts()
  if (currentReceipt.expenseDate) {
    const inputDate = new Date(currentReceipt.expenseDate.replace(/-/g, '/'))
    const nowDate = new Date()
    if (inputDate <= nowDate) {
      return true
    } else {
      return false
    }
  }
}

const schema = object({
  expenseDate: date()
    .required('必須です')
    .typeError('必須です')
    .test(
      'date_validation',
      '支出年月日には現在時刻より過去の年月日を選択してください',
      dateComparison,
    ),
  expenseTypeId: array()
    .min(1)
    .max(1)
    .required('必須です'),
  expenseAmount: number()
    .required('必須です')
    .positive('正の整数で入力してください')
    .integer('整数で入力してください')
    .typeError('整数で入力してください'),
  expenseContent1: string().required('必須です'),
  expenseContent2: string().nullable(),
  files: array(),
})

export const useReceiptForm = () => {
  const { currentReceipt, getReceiptsAfterResponse } = useReceipts()
  const { getNotificationsAfterResponse } = useNotifications()
  const { getTimelineAfterResponse } = useLogs()
  const { getTopDataAfterResponse } = useTopData()
  const { receiptDialog } = useReceiptDialog()
  const { toggleShowLoader } = useLoader()
  const { toggleShowError } = useError()
  const route = useRoute()
  watch(receiptDialog, () => {
    if (receiptDialog.isPreview) {
      currentReceipt.expenseDate = dayjs(expenseDate.value).format(
        'YYYY-MM-DD HH:mm:ss',
      )
      currentReceipt.expenseTypeId = [...expenseTypeId.value]
      currentReceipt.expenseAmount = expenseAmount.value
      currentReceipt.expenseContent1 = expenseContent1.value
      currentReceipt.expenseContent2 = expenseContent2.value
      currentReceipt.denominator = denominator.value
      currentReceipt.numerator = numerator.value
      currentReceipt.isDistributed = isDistributed.value
      // currentReceipt.sendBack = [...sendBack.value];
    }
    if (receiptDialog.isPreviewEnabled) {
      tabs[0].status = 1
    } else {
      tabs[0].status = 0
    }
  })

  const { meta } = useForm({ validationSchema: schema })

  // onMounted(() => {
  // TODO: 初期データ読み込み時にisPreviewEnableを更新する
  //   receiptDialog.isPreviewEnabled = meta.valid
  // }),
  watch(meta, meta => {
    meta.valid
      ? (receiptDialog.isPreviewEnabled = true)
      : (receiptDialog.isPreviewEnabled = false)
  })

  const { value: expenseDate, errorMessage: expenseDateError } = useField(
    'expenseDate',
    undefined,
    {
      initialValue: currentReceipt.expenseDate
        ? dayjs(currentReceipt.expenseDate).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
    },
  )

  const { value: expenseTypeId, errorMessage: expenseTypeIdError } = useField(
    'expenseTypeId',
    undefined,
    {
      initialValue: currentReceipt.expenseTypeId,
    },
  )

  const { value: expenseAmount, errorMessage: expenseAmountError } = useField(
    'expenseAmount',
    undefined,
    {
      initialValue: currentReceipt.expenseAmount,
    },
  )
  const { value: denominator, errorMessage: denominatorError } = useField(
    'denominator',
    undefined,
    {
      initialValue: currentReceipt.denominator,
    },
  )
  const { value: numerator, errorMessage: numeratorError } = useField(
    'numerator',
    undefined,
    {
      initialValue: currentReceipt.numerator,
    },
  )
  const {
    value: expenseContent1,
    errorMessage: expenseContent1Error,
  } = useField('expenseContent1', undefined, {
    initialValue: currentReceipt.expenseContent1,
  })

  const {
    value: expenseContent2,
    errorMessage: expenseContent2Error,
  } = useField('expenseContent2', undefined, {
    initialValue: currentReceipt.expenseContent2,
  })
  const { value: isDistributed, errorMessage: isDistributedError } = useField(
    'isDistributed',
    undefined,
    {
      initialValue: currentReceipt.isDistributed ?? false,
    },
  )

  const { value: files, errorMessage: filesError } = useField(
    'files',
    undefined,
    { initialValue: currentReceipt.files },
  )
  const { value: sendBack, errorMessage: sendBackError } = useField(
    'sendBack',
    undefined,
    {
      initialValue: currentReceipt.sendBack,
    },
  )

  const handleImages = files => {
    if (files.length > 0) {
      // tabs[1].required = true
      // tabs[1].addFilesBtn = true
      tabs[1].status = 1
    } else {
      // tabs[1].required = false
      // tabs[1].addFilesBtn = false
      tabs[1].status = 0
    }
  }

  const getSentBackItemMessage = attr => {
    const sentBackList = []
    if (
      currentReceipt.option.viewType === 0 ||
      currentReceipt.option.viewType === 1 ||
      currentReceipt.sentBackItems.length == 0
    ) {
      return []
    }
    currentReceipt.sentBackItems.forEach(item => {
      if (!item || item.length == 0) return []
      const backItem = item.items.find(item => item.target == attr)
      if (backItem !== undefined) {
        sentBackList.push(backItem)
      }
    })
    if (sentBackList.length > 0) {
      sentBackList
      return sentBackList
    }
    return []
  }

  watch(expenseDate, () => {
    currentReceipt.expenseDate = dayjs(expenseDate.value).format(
      'YYYY-MM-DD HH:mm:ss',
    )
  })
  watch(expenseAmount, () => {
    currentReceipt.expenseAmount = expenseAmount.value
  })
  watch(expenseContent1, () => {
    currentReceipt.expenseContent1 = expenseContent1.value
  })
  watch(expenseContent2, () => {
    currentReceipt.expenseContent2 = expenseContent2.value
  })
  watch(denominator, () => {
    currentReceipt.denominator = denominator.value
  })
  watch(numerator, () => {
    currentReceipt.numerator = numerator.value
  })
  watch(expenseTypeId, () => {
    currentReceipt.expenseTypeId = expenseTypeId.value
  })
  watch(isDistributed, () => {
    currentReceipt.isDistributed = isDistributed.value
  })

  /**
   * 諸経費を新規作成/更新する
   *
   * @param {number|null} id approve_history.document_id 新規作成はnull指定
   * @param {string} type receipt: 一時保存, receipt-apply: 適用
   */
  const submitReceipt = async (id, type) => {
    try {
      toggleShowLoader(true)
      if (!(type === 'receipt' || type === 'receipt-apply')) {
        throw new Error('invalid type: ' + type)
      }
      const payload = {
        is_temporary: type === 'receipt',
        expense_type_id: currentReceipt.expenseTypeId[0]?.id,
        expense_date: currentReceipt.expenseDate,
        expense_amount: currentReceipt.expenseAmount,
        expense_content1: currentReceipt.expenseContent1,
        expense_content2: currentReceipt.expenseContent2,
        is_distributed: currentReceipt.isDistributed,
        numerator: currentReceipt.numerator,
        denominator: currentReceipt.denominator,
        images: currentReceipt.files,
      }
      const isPost = id === null
      const response = isPost
        ? await axios.post(`/receipts`, payload)
        : await axios.put(`/receipts/${id}`, payload)
      if (route.name === 'Receipts') {
        await getReceiptsAfterResponse(response)
      } else if (route.name !== 'Receipts') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(response)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(response)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(response)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const cancelRequest = async id => {
    try {
      toggleShowLoader(true)
      const response = await axios.post(`/receipts/cancel/${id}`)
      if (route.name === 'Receipts') {
        await getReceiptsAfterResponse(response)
      } else if (route.name !== 'Receipts') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(response)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(response)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(response)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const approveRequest = async id => {
    try {
      toggleShowLoader(true)
      const { rejectedAttrs, toSnake } = useReceiptReject()
      let data = {}
      if (rejectedAttrs.length !== 0) {
        toSnake()
        data = {
          sent_back_items: rejectedAttrs,
        }
      }
      const response = await axios.post(`/receipts/approve/${id}`, data)
      if (route.name === 'Receipts') {
        await getReceiptsAfterResponse(response)
      } else if (route.name !== 'Receipts') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(response)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(response)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(response)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  /**
   * 按分計算し、計算した結果にカンマ区切りをつけた文字列を返す
   *
   * @returns {string}
   */
  const prorateCalc = () => {
    const amount = currentReceipt.expenseAmount
    const denominator = currentReceipt.denominator
    const numerator = currentReceipt.numerator
    const amountStr = amount.toLocaleString()
    if (denominator == 0 || numerator == 0) {
      return `${amountStr}円`
    }
    const proratedAmount = Math.floor((numerator * amount) / denominator)
    const proratedStr = proratedAmount.toLocaleString()
    return `${proratedStr}円 [${amountStr}円 按分 ${numerator}/${denominator}]`
  }

  return {
    expenseTypes,
    handleImages,
    getSentBackItemMessage,
    submitReceipt,
    cancelRequest,
    approveRequest,
    prorateCalc,
    meta,
    expenseDate,
    expenseDateError,
    expenseTypeId,
    expenseTypeIdError,
    expenseAmount,
    expenseAmountError,
    denominator,
    denominatorError,
    numerator,
    numeratorError,
    expenseContent1,
    expenseContent1Error,
    expenseContent2,
    expenseContent2Error,
    isDistributed,
    isDistributedError,
    files,
    filesError,
    sendBack,
    sendBackError,
  }
}
